<template>
  <div>
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`line-item-num-${index + 1}-pickup-stop`"
      :errors="v$.pickupStopSequenceNumber.$errors"
      :label="t('shipments.paramStop', { param: t('shipments.pickups') })"
      :warning="pickupStopWarning"
      class="w-6"
    >
      <RygenDropdown
        :id="inputId"
        v-model="internalValue.pickupStopSequenceNumber"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="disabled || !availablePicks.length"
        :placeholder="
          t('common.selectParam', {
            param: t('shipments.paramStop', {
              param: t('shipments.pickups'),
            }),
          })
        "
        :options="availablePicks"
        option-value="sequenceNumber"
        append-to="body"
      >
        <template #option="{ option }">
          <StopLabel :stop="option" />
        </template>

        <template v-if="selectedPickupStop" #value>
          <StopLabel :stop="selectedPickupStop" />
        </template>
      </RygenDropdown>
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`line-item-num-${index + 1}-dropoff-stop`"
      :errors="v$.dropOffStopSequenceNumber.$errors"
      :label="t('shipments.paramStop', { param: t('shipments.dropoffs') })"
      :warning="dropOffStopWarning"
      class="w-6"
    >
      <RygenDropdown
        :id="inputId"
        v-model="internalValue.dropOffStopSequenceNumber"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="disabled || !availableDrops.length"
        :placeholder="
          t('common.selectParam', {
            param: t('shipments.paramStop', {
              param: t('shipments.dropoffs'),
            }),
          })
        "
        :options="availableDrops"
        option-value="sequenceNumber"
        append-to="body"
      >
        <template #option="{ option }">
          <StopLabel :stop="option" />
        </template>

        <template v-if="selectedDropoffStop" #value>
          <StopLabel :stop="selectedDropoffStop" />
        </template>
      </RygenDropdown>
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { Optional } from '@rygen/rygen-ui/model';

import { LineItem, Shipment, ShipmentLineItem, ShipmentStop } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import StopLabel from '@/components/location/StopLabel.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  modelValue: {
    type: ShipmentLineItem,
    default: () => new ShipmentLineItem(),
  },
  index: {
    type: Number,
    default: () => 1,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, index, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();

const internalValue: Ref<ShipmentLineItem> = ref(modelValue.value);
const rules = {
  pickupStopSequenceNumber: {
    required: required(
      t('shipments.paramStop', { param: t('shipments.pickups') }),
    ),
  },
  dropOffStopSequenceNumber: {
    required: required(
      t('shipments.paramStop', { param: t('shipments.dropoffs') }),
    ),
  },
};
const { v$ } = useValidations(internalValue, rules);

const pickupStops = computed(() => {
  return parentEntity.value instanceof Shipment
    ? parentEntity.value.stops.filter((s) => s.isPickup)
    : [];
});

const availablePicks = computed(() => {
  const maxSeqNum = internalValue.value.dropOffStopSequenceNumber;
  return maxSeqNum
    ? pickupStops.value.filter(
        (pick) => !!pick.sequenceNumber && pick.sequenceNumber < maxSeqNum,
      )
    : pickupStops.value;
});

const dropoffStops = computed(() => {
  return parentEntity.value instanceof Shipment
    ? parentEntity.value.stops.filter((s) => s.isDropOff)
    : [];
});

const availableDrops = computed(() => {
  const minSeqNum = internalValue.value.pickupStopSequenceNumber;
  return minSeqNum
    ? dropoffStops.value.filter(
        (drop) => !!drop.sequenceNumber && drop.sequenceNumber > minSeqNum,
      )
    : dropoffStops.value;
});

const selectedPickupStop = computed(() => {
  const l = internalValue.value;
  return l instanceof ShipmentLineItem
    ? pickupStops.value.find(
        (s) => s.sequenceNumber === l.pickupStopSequenceNumber,
      )
    : undefined;
});

const selectedDropoffStop = computed(() => {
  const l = internalValue.value;
  return l instanceof ShipmentLineItem
    ? dropoffStops.value.find(
        (s) => s.sequenceNumber === l.dropOffStopSequenceNumber,
      )
    : undefined;
});

const pickupStopWarning = computed(() => {
  if (availablePicks.value.length) {
    return undefined;
  }

  return selectedDropoffStop.value
    ? t('shipments.entry.addPickupStopBeforeParam', {
        param: generateStopName(selectedDropoffStop.value),
      })
    : t('common.addParam', {
        param: t('shipments.paramStop', { param: t('shipments.pickups') }),
      });
});

const dropOffStopWarning = computed(() => {
  if (availableDrops.value.length) {
    return undefined;
  }

  return selectedPickupStop.value
    ? t('shipments.entry.addDropOffStopAfterParam', {
        param: generateStopName(selectedPickupStop.value),
      })
    : t('common.addParam', {
        param: t('shipments.paramStop', { param: t('shipments.dropoffs') }),
      });
});

const generateStopName = (stop: ShipmentStop) => {
  const stopNumber = t('common.paramNum', {
    param: t('shipments.stops'),
    num: stop.sequenceNumber,
  });

  const locationName = [
    stop.location?.locationName,
    stop.location?.city,
    stop.location?.state,
  ]
    .filter((x) => !!x?.trim())
    .join(', ');

  return locationName ? `${stopNumber} - ${locationName}` : stopNumber;
};

watch(
  () => internalValue.value.pickupStopSequenceNumber,
  (newVal: Optional<number>) => {
    if (newVal) {
      const pick = availablePicks.value.find(
        (p) => p.sequenceNumber === newVal,
      );
      internalValue.value.pickupStopId = pick?.id;
    } else {
      internalValue.value.pickupStopId = undefined;
    }
  },
);

watch(
  () => internalValue.value.dropOffStopSequenceNumber,
  (newVal: Optional<number>) => {
    if (newVal) {
      const drop = availableDrops.value.find(
        (d) => d.sequenceNumber === newVal,
      );
      internalValue.value.dropOffStopId = drop?.id;
    } else {
      internalValue.value.dropOffStopId = undefined;
    }
  },
);

watch(
  availablePicks,
  (newVal) => {
    nextTick(() => {
      if (
        newVal.length === 1 &&
        !internalValue.value.pickupStopSequenceNumber
      ) {
        const [pickup] = newVal;
        internalValue.value.pickupStopSequenceNumber = pickup.sequenceNumber;
        internalValue.value.pickupStopId = pickup.id;
      }
    });
  },
  { deep: true, immediate: true },
);

watch(
  availableDrops,
  (newVal) => {
    nextTick(() => {
      if (
        newVal.length === 1 &&
        !internalValue.value.dropOffStopSequenceNumber
      ) {
        const [dropoff] = newVal;
        internalValue.value.dropOffStopSequenceNumber = dropoff.sequenceNumber;
        internalValue.value.dropOffStopId = dropoff.id;
      }
    });
  },
  { deep: true, immediate: true },
);

watch(
  modelValue,
  (newVal: ShipmentLineItem) => {
    internalValue.value = new ShipmentLineItem(newVal);
    v$.value.$reset();
  },
  { immediate: true, deep: true },
);

watch(
  internalValue,
  (newVal: ShipmentLineItem) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>
